import { useState, useEffect, FormEvent, RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Input from '@gym-atoms/Input/Input';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import { useForm } from 'react-hook-form';
import Alert, { AlertType, createAlert } from '@gym-atoms/Alert/Alert';
import {
  GymChainAccessCategoryInput,
  LambdaResponse,
  RelatedProgram,
  ProgramAccessCategorySite,
  AddProgramAccessCategoryInput
} from '@gym-src/API';
import { API } from 'aws-amplify';
import {
  addGymChainAccessCategory,
  addProgramAccessCategory,
  modifyGymChainAccessCategory,
  removeProgramAccessCategory
} from '@gym-graphql/mutations';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import { GymChainAccessCategory } from '@gym-particles/types/models';
import Loader from '@gym-atoms/Loader/Loader';
import { getAllPrograms, getProgramAccessCategories } from '@gym-graphql/queries';
import { GymChainProgramAccessCategory } from '../../../particles/types/GymChainProgramAccessCategory';
import { fetchGymChainProgramAccessCategory } from '../../../redux/slices/gymChainProgramAccessCategorySlice';

const GymChainProgramAccessCategoryInfoTemplate = (
  props: GymChainProgramAccessCategoryInfoTemplateProps
) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { gymChainProgramAccessCategoryId, chainId } = useParams<{
    gymChainProgramAccessCategoryId: string;
    chainId: string;
  }>();
  const dispatch = useAppDispatch();
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const [programsDropDownItems, setProgramsDropDownItems] = useState<Array<SelectItem>>([]);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedPrograms, setDefaultSelectedPrograms] = useState<
    Array<ProgramAccessCategorySite>
  >([]);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const userId = useAppSelector((state) => state.user.userId);
  const userRole = useAppSelector((state) => state.user.userRole);
  const [isInserted, setIsInserted] = useState(false);
  const [schedulesOnly, setSchedulesOnly] = useState(false);
  const [sortField, setSortField] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState(-1);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [searchTerm, setSearchTerm] = useState('');

  const getTranslationKey = (e: string): string => {
    return props.type === 'new' ? 'ADD_NEW_ACCESS_CATEGORY.' + e : 'MODIFY_ACCESS_CATEGORY.' + e;
  };

  type FormInputs = {
    id?: number;
    siteId?: number;
    name?: string;
    gender?: string;
    age?: number;
    schedulesOnly?: string;
    status?: string;
    programs?: Array<number>;
    arxAccessCategoryId?: string;
  };

  const defaultValues: FormInputs = {
    id: props.programAccessCategory?.id || 0,
    siteId: props.programAccessCategory?.siteId || 0,
    name: props.programAccessCategory?.name || '',
    gender: props.programAccessCategory?.gender || '',
    age: props.programAccessCategory?.age || 0,
    schedulesOnly: props.programAccessCategory?.schedulesOnly ? 'true' : 'false',
    status: props.programAccessCategory?.status || '',
    programs: [],
    arxAccessCategoryId: props.programAccessCategory?.arxAccessCategoryId || ''
  };

  const genderDropDownItems = [
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_MALE')),
      value: 'male'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_FEMALE')),
      value: 'female'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_NA')),
      value: 'n/a'
    }
  ];

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof GymChainAccessCategoryInput, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
  };

  const [formValues, setFormValues] = useState(defaultValues);

  const { register, handleSubmit, setValue, errors } = useForm<FormInputs>({
    defaultValues: defaultValues
  });

  const DeleteDialogFooter = () => {
    return (
      <div>
        <Button
          label={t('COMMON.OK')}
          variant={ButtonVariants.basic}
          onClick={() => setIsDeleteDialogVisible(false)}
        />
      </div>
    );
  };
  const RemovableGymDialog = () => {
    return (
      <div>
        <DialogBox
          dialogVisible={isDeleteDialogVisible}
          variant={DialogBoxVariants.long}
          dialogDismissableMask={true}
          onHideCallback={() => setIsDeleteDialogVisible(false)}
          dialogFooter={<DeleteDialogFooter />}
          dialogClosable={false}
          dialogHeader={t('MEMBERSHIP_LIST.TABLE.DELETE_MODAL_HEADER')}
          content={<Text>{t('MEMBERSHIP_LIST.TABLE.DELETE_GYM_MODAL_CANNOT')}</Text>}
        />
      </div>
    );
  };

  const getGymPrograms = async () => {
    try {
      console.log('defaultSelectedPrograms', defaultSelectedPrograms);
      const siteProgramsResponse = await (API.graphql({
        query: getAllPrograms,
        variables: {
          siteId: chainId
        }
      }) as Promise<{
        data: { getAllPrograms: RelatedProgram[] };
      }>);
      const siteProgramsDropDownItems =
        siteProgramsResponse.data.getAllPrograms
          .map((data) => {
            return {
              label: data.programName + ' (' + data.importedId + ')' || '',
              value: data.id
            };
          })
          .filter((item) => item !== undefined) || [];
      console.log({ siteProgramsDropDownItems });
      setProgramsDropDownItems(siteProgramsDropDownItems);
      props.type === 'modify' && getDefaultSelectedPrograms();
      console.log({ formValues });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getDefaultSelectedPrograms = async () => {
    console.log('defaultSelectedPrograms', gymChainProgramAccessCategoryId);
    try {
      const defaultProgramsResponse = await (API.graphql({
        query: getProgramAccessCategories,
        variables: {
          accessCategoryId: +gymChainProgramAccessCategoryId
        }
      }) as Promise<{
        data: { getProgramAccessCategories: ProgramAccessCategorySite[] };
      }>);
      console.log({ selectedPrograms: defaultProgramsResponse.data });

      setDefaultSelectedPrograms(defaultProgramsResponse.data.getProgramAccessCategories);
    } catch (error) {
      console.error('Failed to fetch selected programs', error);
    }
  };

  useEffect(() => {
    getGymPrograms();
    dispatch(
      fetchGymChainProgramAccessCategory({
        siteId: props.programAccessCategory?.siteId || 0
      })
    );
  }, []);

  const programsDropdownHandler = async (e: DropdownChangeEvent) => {
    setValue('programs', e.value);
    setFormValues({ ...formValues, programs: e.value });
  };

  useEffect(() => {
    setFormValues({
      ...formValues,
      id: props.programAccessCategory?.id || 0,
      siteId: props.programAccessCategory?.siteId || 0,
      name: props.programAccessCategory?.name || '',
      gender: props.programAccessCategory?.gender || '',
      schedulesOnly: props.programAccessCategory?.schedulesOnly ? 'true' : 'false',
      status: props.programAccessCategory?.status || '',
      programs: defaultSelectedPrograms.map((program) => program.programId),
      arxAccessCategoryId: props.programAccessCategory?.arxAccessCategoryId || ''
    });
    setValue('name', props.programAccessCategory?.name);
    setValue('siteId', props.programAccessCategory?.siteId);
    setValue('gender', props.programAccessCategory?.gender);
    setValue('age', props.programAccessCategory?.age);
    setValue('schedulesOnly', props.programAccessCategory?.schedulesOnly);
    setValue('status', props.programAccessCategory?.status);
    console.log({ programs: defaultSelectedPrograms.map((program) => program.programId) });
    console.log({ formValues: formValues.programs });
    setValue(
      'programs',
      defaultSelectedPrograms.map((program) => program.programId)
    );
    setValue('arxAccessCategoryId', props.programAccessCategory?.arxAccessCategoryId);
  }, [props, defaultSelectedPrograms]);

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  const removeLocationAccessCategory = async (programAccessCategoryId: number) => {
    try {
      await API.graphql({
        query: removeProgramAccessCategory,
        variables: {
          id: programAccessCategoryId
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addProgramAccessCategories = async (
    arxAccessCategoryId: string,
    programs: number[],
    addAccessCategoryResponse: GymChainAccessCategory
  ) => {
    try {
      console.log({ programs });
      console.log({ addAccessCategoryResponse });
      const addProgramAccessCategoryInput: AddProgramAccessCategoryInput = {
        name: addAccessCategoryResponse.name.replace(/'/g, "''"),
        programIds: Array.isArray(programs) ? programs.map((id) => +id) : [programs],
        gender: addAccessCategoryResponse.gender,
        age: addAccessCategoryResponse.age,
        accessCategoryId: addAccessCategoryResponse.id,
        arxAccessCategoryId: arxAccessCategoryId,
        status: addAccessCategoryResponse.status,
        createdBy: userId,
        lastModifiedBy: userId,
        programs: []
      };

      addProgramAccessCategoryInput.programs = addProgramAccessCategoryInput.programIds.map(
        (id) => programsDropDownItems.find((m) => m.value === id)?.label || ''
      );
      console.log({ addProgramAccessCategoryInput });

      try {
        const result = await (API.graphql({
          query: addProgramAccessCategory,
          variables: {
            input: addProgramAccessCategoryInput
          }
        }) as Promise<{
          data: { addProgramAccessCategory: LambdaResponse };
        }>);
        console.log(result);
        if (result.data.addProgramAccessCategory.error) {
          throw result.data.addProgramAccessCategory;
        }
        console.log(result);
      } catch (error) {
        setFailedDialogVisible(true);
        console.log(error);
      }
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addSiteAccessCategory = async (data: FormInputs) => {
    console.log('addAccessCategory:', data);
    const addAccessCategoryInput: GymChainAccessCategoryInput = {
      siteId: +chainId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: +data.age || 0,
      schedulesOnly: false,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };
    console.log({ addAccessCategoryInput });

    try {
      const addAccessCategoryResponse = await (API.graphql({
        query: addGymChainAccessCategory,
        variables: {
          input: addAccessCategoryInput
        }
      }) as Promise<{
        data: { addGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ addAccessCategoryResponse });
      console.log(data.programs);

      if (data.programs.length > 0) {
        await addProgramAccessCategories(
          data.arxAccessCategoryId,
          data.programs,
          addAccessCategoryResponse.data.addGymChainAccessCategory
        );
      }
      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  const modifyAccessCategory = async (data: FormInputs) => {
    console.log('modify', { data });
    const modifyGymChainAccessCategoryInput: GymChainAccessCategoryInput = {
      id: props.programAccessCategory.id,
      siteId: props.programAccessCategory.siteId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: data.age || 0,
      schedulesOnly: schedulesOnly,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };

    try {
      const modifyGymAccessCategoryResponse = await (API.graphql({
        query: modifyGymChainAccessCategory,
        variables: {
          input: modifyGymChainAccessCategoryInput
        }
      }) as Promise<{
        data: { modifyGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ modifyGymAccessCategoryResponse });
      const addedPrograms = data.programs?.filter(
        (x: number) =>
          !defaultSelectedPrograms.map((y: ProgramAccessCategorySite) => y.programId).includes(+x)
      );
      console.log({ addedPrograms });

      if (addedPrograms.length > 0) {
        await addProgramAccessCategories(
          data.arxAccessCategoryId,
          addedPrograms,
          modifyGymAccessCategoryResponse.data.modifyGymChainAccessCategory
        );
      }
      const removedPrograms = defaultSelectedPrograms
        .map((y: ProgramAccessCategorySite) => y.programId)
        .filter((x: number) => !data.programs?.includes(Number(x)));
      console.log({ removedPrograms });

      removedPrograms?.forEach(async (g: number) => {
        const ProgramRemoved = defaultSelectedPrograms.find(
          (i: ProgramAccessCategorySite) => i.programId === g
        )?.programAccessCategoryId;
        console.log({ ProgramRemoved });

        if (ProgramRemoved > 0) {
          await removeLocationAccessCategory(ProgramRemoved);
        }
      });
      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  //Form submission handler
  const onSubmitHandler = async (data: FormInputs) => {
    setIsInserted(true);
    if (props.type === 'new') {
      await addSiteAccessCategory(data);
    } else if (props.type === 'modify' && props.programAccessCategory) {
      await modifyAccessCategory(data);
    }
    setIsInserted(false);
  };

  useEffect(() => {
    register('name', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string,
      maxLength: { value: 255, message: 'required' as string }
    });
    register('gender');
    register('age');
    register('schedulesOnly');
    register('status', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('programs');
    register('arxAccessCategoryId');
  }, [register]);

  // TO-DO [Suggestion] - Keep these values in one place as enums.

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    setValue(e.target.name as keyof GymChainAccessCategoryInput, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };
  const statusDropDownItems = [
    {
      label: t(getTranslationKey('FORM_STATUS_DROPDOWN_ACTIVE')),
      value: 'active'
    },
    { label: t(getTranslationKey('FORM_STATUS_DROPDOWN_INACTIVE')), value: 'inactive' }
  ];
  const statusDropdownHandler = (e: DropdownChangeEvent) => {
    setValue('status', e.value);
    setFormValues({ ...formValues, status: e.value });
  };

  const changeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof FormInputs, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
    console.log({ formValues });
  };
  const loaderElement = () => {
    return (
      <div className="loaderHeader">
        <div className="loaderContent">
          <Loader shape="rectangle" width="quarter" />
        </div>
        <div>
          <Loader shape="rectangle" width="full" />
        </div>
      </div>
    );
  };
  return (
    <div className="row">
      <div className="d-flex flex-row">
        <div className="col">
          <Alert refProp={props.alertRef} />
        </div>
      </div>
      {isAccessCategoriesLoading ? (
        [...Array(5)].map((element, index) => {
          return <div key={index}> {loaderElement()} </div>;
        })
      ) : (
        <form className="input-mb20" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_LABEL'))}
                variant="basic"
                name="name"
                value={formValues.name}
                onChange={inputFieldChangeHandler}
                error={errors.name ? true : false}
                errorMessage={errors.name?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_PLACEHOLDER'))}
                required={true}
              />
            </div>
          </div>
          <div className="col-12">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_LABEL'))}
            </label>
            <Dropdown
              name="gender"
              options={genderDropDownItems}
              value={formValues.gender}
              onChange={dropdownChangeHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_PLACEHOLDER'))}
              error={errors.gender ? true : false}
            />
          </div>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_LABEL'))}
                variant="basic"
                name="age"
                value={formValues.age > 0 ? formValues.age.toString() : ''}
                onChange={inputFieldChangeHandler}
                error={errors.age ? true : false}
                errorMessage={errors.age?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_PLACEHOLDER'))}
                required={false}
              />
            </div>
          </div>
          <div className="col-12">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_LABEL'))}
              <span className="p-error"> *</span>
            </label>
            <Dropdown
              name="status"
              options={statusDropDownItems}
              value={formValues.status}
              onChange={statusDropdownHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_PLACEHOLDER'))}
              error={errors.status ? true : false}
            />
          </div>
          <div className="mb25">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_PROGRAM_LABEL'))}
            </label>
            <Dropdown
              key={programsDropDownItems.length > 0 ? 'loaded' : 'loading'}
              chips={true}
              name="programs"
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_PROGRAM_PLACEHOLDER'))}
              value={formValues.programs}
              options={programsDropDownItems}
              onChange={programsDropdownHandler}
              error={errors.programs ? true : false}
            />
          </div>
          {formValues.programs?.length > 0 && (
            <div className="mb25">
              <h4>Selected programs:</h4>
              <h3>{formValues.programs?.length} Programs selected</h3>
              <div>
                {formValues.programs?.map((name, index) => (
                  <span
                    key={index}
                    className="p-badge"
                    style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                    {programsDropDownItems.find((obj) => obj.value === name).label}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_LABEL'))}
                variant="basic"
                name="arxAccessCategoryId"
                value={formValues.arxAccessCategoryId}
                onChange={inputFieldChangeHandler}
                error={errors.name ? true : false}
                errorMessage={errors.name?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_PLACEHOLDER'))}
                required={false}
                disabled={formValues.programs.length === 0}
              />
            </div>
          </div>
          <div className="d-flex flex-row mt35 btn-min-w-110">
            <div className="mr15">
              <Button
                data-cy={'membership-info-temp-form-btn-save'}
                icon={isInserted ? 'pi-spinner pi-spin' : ''}
                label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                size={ButtonSizes.medium}
                disabled={isInserted}
              />
            </div>
            <Button
              data-cy={'membership-info-temp-form-btn-cancel'}
              label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
              variant={ButtonVariants.textonly}
              onClick={() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`)}
              disabled={isInserted}
            />
            <ActionFailedDialog />
            <RemovableGymDialog />
          </div>
        </form>
      )}
    </div>
  );
};

export default GymChainProgramAccessCategoryInfoTemplate;

type GymChainProgramAccessCategoryInfoTemplateType = 'new' | 'modify';

export interface GymChainProgramAccessCategoryInfoTemplateProps {
  type: GymChainProgramAccessCategoryInfoTemplateType;
  programAccessCategory?: GymChainProgramAccessCategory;
  alertRef: RefObject<AlertType>;
  alertMessage: string;
}
